import React from "react";
import { ALL_TESTIMONIALS } from "@/constants/testimonials";
import { TestimonialScrollRow } from "@/components/TestimonialScrollRow";

const StarRating = ({ rating = 5 }) => {
  const n = Math.min(5, Math.max(1, Number(rating) || 5));
  return (
    <div className="flex gap-0.5 mb-3">
      {Array.from({ length: n }, (_, i) => (
        <svg key={i} width="16" height="16" viewBox="0 0 24 24" fill="#C9973A" aria-hidden>
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </div>
  );
};

const PatientTestimonials = ({ procedureName }) => {
  const needle = (procedureName || "").toLowerCase();
  const relevant = ALL_TESTIMONIALS.filter((t) =>
    t.procedure.toLowerCase().includes(needle)
  );
  const others = ALL_TESTIMONIALS.filter(
    (t) => !t.procedure.toLowerCase().includes(needle)
  );
  const display = [...relevant, ...others];

  return (
    <section className="bg-warm-white pt-10 pb-16 md:pt-12 md:pb-24">
      <div className="max-w-7xl mx-auto px-4 md:px-8 min-w-0">
        <div className="text-center mb-10 md:mb-12 px-1">
          <p className="text-gold text-xs tracking-[0.2em] uppercase mb-3 font-semibold">
            Patient Experiences
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-premium-purple tracking-tight mb-3">
            What Our Patients Say
          </h2>
          <p className="text-deep-navy/65 text-[15px] max-w-xl mx-auto leading-reading">
            Real experiences from patients in Mumbai. Verified reviews from Google, Practo, HexaHealth, and Eka Care.
          </p>
        </div>

        <div className="relative -mx-4 md:-mx-8 min-w-0 px-4 md:px-8">
          <TestimonialScrollRow className="pb-2" data-testid="patient-testimonials-scroll">
            {display.map((t) => (
              <div
                key={t.id}
                className="testimonial-card shrink-0 grow-0 snap-start snap-always flex flex-col bg-white border border-deep-navy/10 rounded-xl p-6 sm:p-7 max-sm:basis-[min(88vw,320px)] sm:basis-[calc((100%-3.75rem)/3.5)]"
              >
                <StarRating rating={t.rating} />
                <p className="text-deep-navy text-[15px] leading-reading mb-5 flex-1 italic">
                  "{t.text}"
                </p>
                <div className="flex items-center justify-between gap-3 pt-4 border-t border-deep-navy/10 mt-auto">
                  <div className="min-w-0">
                    <p className="text-deep-navy font-bold text-sm leading-snug">{t.name}</p>
                    <p className="text-deep-navy/55 text-xs leading-snug mt-0.5">{t.procedure}</p>
                  </div>
                  <span className="shrink-0 text-gold text-[11px] font-semibold tracking-wide bg-gold/10 px-3 py-1 rounded-full">
                    {t.source}
                  </span>
                </div>
              </div>
            ))}
          </TestimonialScrollRow>
        </div>

        <div className="mt-10 md:mt-12 bg-premium-purple rounded-xl px-6 py-8 md:px-10 flex flex-wrap items-center justify-around gap-6">
          {[
            { number: "94%", label: "Patient Satisfaction" },
            { number: "99%", label: "Would Recommend" },
            { number: "17+", label: "Years Experience" },
            { number: "5*", label: "Justdial Rating" },
          ].map((stat, i) => (
            <div key={i} className="text-center min-w-[5.5rem]">
              <p className="text-gold text-2xl md:text-3xl font-bold mb-1">{stat.number}</p>
              <p className="text-warm-white/65 text-[11px] tracking-wider uppercase">
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PatientTestimonials;
